import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Mail, MessageCircle, Phone } from 'lucide-react';
import { StepHeader, WizField, WizInput, WizTextarea, WizSwitch } from '../wizardUi';

const ESCALATION_OPTIONS = [
  { id: 'email', icon: Mail, labelKey: 'escalationEmail' },
  { id: 'whatsapp', icon: MessageCircle, labelKey: 'escalationWhatsapp' },
  { id: 'phone', icon: Phone, labelKey: 'escalationPhone' },
  { id: 'other', icon: null, labelKey: 'escalationOther' },
];

export default function Step4UseCases({ wizard }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { orders, setOrdersField } = wizard;

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOrdersField('catalogFileName', file.name);
  }

  const modes = [
    { id: 'orders', label: t('wizardIg.segOrders') },
    { id: 'reservations', label: t('wizardIg.segReservations') },
  ];

  return (
    <div className="flex flex-col gap-5 lg:mx-auto lg:max-w-2xl lg:gap-6">
      <StepHeader eyebrow={t('wizardIg.eyebrow', { n: 4 })} title={t('wizardIg.title3')} />

      <div role="tablist" className="inline-flex gap-1 rounded-full border border-white/[0.08] bg-white/[0.04] p-1">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={orders.mode === m.id}
            onClick={() => setOrdersField('mode', m.id)}
            className={`h-11 flex-1 rounded-full px-4 text-[13.5px] font-medium transition-all ${
              orders.mode === m.id
                ? 'bg-primary text-primary-foreground shadow-[0_0_18px_rgba(124,58,237,0.5)]'
                : 'text-white/55 hover:text-white'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {orders.mode === 'orders' ? (
        <>
          <p className="text-[13.5px] leading-relaxed text-white/55">{t('wizardIg.useCaseOrdersDesc')}</p>

          <WizField label={t('wizardIg.uploadCatalogTitle')}>
            <p className="text-[12.5px] leading-relaxed text-white/45">{t('wizardIg.catalogExplain')}</p>

            <div className="mt-1 flex items-center justify-between gap-3 rounded-[18px] border border-white/[0.08] bg-white/[0.03] px-4 py-3">
              <span className="text-[13px] font-medium text-white/80">{t('wizardIg.fewProductsLabel')}</span>
              <WizSwitch
                checked={orders.fewProducts}
                onCheckedChange={(v) => setOrdersField('fewProducts', v)}
                label={t('wizardIg.fewProductsLabel')}
              />
            </div>

            {orders.fewProducts ? (
              <div className="mt-2 flex flex-col gap-1.5">
                <label className="text-[12px] text-white/45">{t('wizardIg.fewProductsHint')}</label>
                <WizTextarea
                  rows={3}
                  value={orders.simpleCatalogText}
                  onChange={(e) => setOrdersField('simpleCatalogText', e.target.value)}
                  placeholder={t('wizardIg.simpleCatalogPlaceholder')}
                />
              </div>
            ) : (
              <>
                <input ref={fileInputRef} type="file" accept=".pdf,.txt" className="hidden" onChange={handleFile} />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 flex min-h-[110px] w-full flex-col items-center justify-center gap-2 rounded-[22px] border-[1.5px] border-dashed border-white/[0.14] p-5 text-center text-white/55 transition-colors hover:border-primary/60 hover:text-white/80"
                >
                  <Upload size={20} />
                  <strong className="text-[13.5px] font-semibold text-foreground">{t('wizardIg.uploadCatalogTitle')}</strong>
                  <span className="text-[12px]">{t('wizardIg.uploadCatalogHint')}</span>
                </button>

                {orders.catalogFileName && (
                  <div className="mt-2 flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 py-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive text-white">
                      <FileText size={16} />
                    </div>
                    <b className="truncate text-[13px] font-semibold">{orders.catalogFileName}</b>
                  </div>
                )}
              </>
            )}
          </WizField>
        </>
      ) : (
        <>
          <p className="text-[13.5px] leading-relaxed text-white/55">{t('wizardIg.useCaseReservationsDesc')}</p>

          <WizField label={t('wizardIg.reservationDetailsLabel')}>
            <WizTextarea
              rows={3}
              value={orders.reservationDetails}
              onChange={(e) => setOrdersField('reservationDetails', e.target.value)}
              placeholder={t('wizardIg.reservationDetailsPlaceholder')}
            />
          </WizField>
        </>
      )}

      <WizField label={t('wizardIg.escalationLabel')}>
        <div className="flex flex-wrap gap-2">
          {ESCALATION_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              aria-pressed={orders.escalation === opt.id}
              onClick={() => setOrdersField('escalation', opt.id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${
                orders.escalation === opt.id
                  ? 'border-primary bg-primary/20 text-white'
                  : 'border-white/[0.1] bg-white/[0.03] text-white/60 hover:border-white/[0.2] hover:text-white/85'
              }`}
            >
              {opt.icon && <opt.icon size={13} />}
              {t(`wizardIg.${opt.labelKey}`)}
            </button>
          ))}
        </div>
        {orders.escalation === 'other' && (
          <WizInput
            value={orders.escalationOther}
            onChange={(e) => setOrdersField('escalationOther', e.target.value)}
            placeholder={t('wizardIg.escalationOtherPlaceholder')}
            className="mt-1"
          />
        )}
      </WizField>
    </div>
  );
}
